import { createFileRoute } from "@tanstack/react-router";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DataTable,
  ExportCsvButton,
  FilterBar,
  SearchInput,
  Section,
  SelectFilter,
  StatCard,
} from "@/components/admin/admin-kit";
import { admLedger } from "@/lib/admin-pro.functions";
import { formatMoney } from "@/lib/behot";
import { privatePageMetadata } from "@/lib/seo";

export const Route = createFileRoute("/nc/live/adm/ledger")({
  head: () => privatePageMetadata("/nc/live/adm/ledger", "Wallet ledger | Control room", "Internal ledger."),
  component: AdminLedger,
});

const WALLETS = [
  { value: "all", label: "All wallets" },
  { value: "topup", label: "Top-up" },
  { value: "earn", label: "Earn" },
  { value: "reward", label: "Reward" },
] as const;

const DIRECTIONS = [
  { value: "all", label: "Credits & debits" },
  { value: "credit", label: "Credits only" },
  { value: "debit", label: "Debits only" },
] as const;

const RANGES = [
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "365", label: "12 months" },
] as const;

function AdminLedger() {
  const run = useServerFn(admLedger);
  const [wallet, setWallet] = useState<(typeof WALLETS)[number]["value"]>("all");
  const [direction, setDirection] = useState<(typeof DIRECTIONS)[number]["value"]>("all");
  const [days, setDays] = useState<(typeof RANGES)[number]["value"]>("30");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const { data, isFetching } = useQuery({
    queryKey: ["adm-ledger", wallet, direction, days, search, page],
    placeholderData: keepPreviousData,
    queryFn: () =>
      run({
        data: {
          wallet: wallet === "all" ? undefined : wallet,
          direction: direction === "all" ? undefined : direction,
          days: Number(days),
          search: search || undefined,
          page,
          pageSize,
        },
      }),
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="Credited" value={formatMoney(data?.creditCents ?? 0)} tone="ok" />
        <StatCard label="Debited" value={formatMoney(data?.debitCents ?? 0)} tone="warn" />
        <StatCard label="Net" value={formatMoney((data?.creditCents ?? 0) - (data?.debitCents ?? 0))} tone="hot" />
      </div>

      <Section
        title="Wallet ledger"
        description="Every movement across top-up, earn and reward wallets."
        actions={<ExportCsvButton rows={rows as unknown as Record<string, unknown>[]} filename="behot-ledger" />}
      >
        <FilterBar>
          <SearchInput value={search} onChange={(v) => { setSearch(v); setPage(0); }} placeholder="Reason or user id…" />
          <SelectFilter label="Wallet" value={wallet} onChange={(v) => { setWallet(v); setPage(0); }} options={WALLETS} />
          <SelectFilter label="Flow" value={direction} onChange={(v) => { setDirection(v); setPage(0); }} options={DIRECTIONS} />
          <SelectFilter label="Range" value={days} onChange={(v) => { setDays(v); setPage(0); }} options={RANGES} />
        </FilterBar>

        <DataTable
          rows={rows}
          rowKey={(r) => r.id}
          empty={isFetching ? "Loading…" : "No ledger movements for this filter."}
          columns={[
            {
              key: "created_at",
              header: "When",
              cell: (r) => new Date(r.created_at).toLocaleString(),
              sortValue: (r) => r.created_at,
            },
            {
              key: "user",
              header: "User",
              cell: (r) => (
                <span>
                  {r.profile?.display_name ?? r.user_id.slice(0, 8)}
                  {r.profile?.slug ? <span className="block text-xs text-muted-foreground">@{r.profile.slug}</span> : null}
                </span>
              ),
            },
            { key: "wallet", header: "Wallet", cell: (r) => r.wallet, sortValue: (r) => r.wallet },
            {
              key: "amount",
              header: "Amount",
              cell: (r) => (
                <span className={r.amount_cents >= 0 ? "text-emerald-600" : "text-destructive"}>
                  {r.amount_cents >= 0 ? "+" : "−"}
                  {formatMoney(Math.abs(r.amount_cents))}
                </span>
              ),
              sortValue: (r) => r.amount_cents,
            },
            {
              key: "balance",
              header: "Balance after",
              cell: (r) => formatMoney(r.balance_after_cents ?? 0),
              sortValue: (r) => r.balance_after_cents ?? 0,
            },
            {
              key: "reason",
              header: "Reason",
              cell: (r) => (
                <span className="text-xs text-muted-foreground">
                  {r.reason}
                  {r.reference_type ? ` · ${r.reference_type}` : ""}
                </span>
              ),
            },
          ]}
        />

        <div className="mt-4 flex items-center justify-between">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            {total.toLocaleString()} movements · page {page + 1} of {pages}
          </span>
          <Button variant="outline" size="sm" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      </Section>
    </div>
  );
}
